import { type FC, memo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/components/podkit/lib/cn";

type MarkdownPreviewProps = {
    content: string;
    className?: string;
};

const MarkdownPreviewBase: FC<MarkdownPreviewProps> = ({ content, className }) => {
    if (!content) return null;

    const components: Components = {
        h1: ({ children, ...props }) => (
            <h1 className="pb-2 text-xl font-medium" {...props}>
                {children}
            </h1>
        ),
        h2: ({ children, ...props }) => (
            <h2 className="pb-1 text-lg font-medium" {...props}>
                {children}
            </h2>
        ),
        h3: ({ children, ...props }) => (
            <h3 className="pb-2 text-md font-medium" {...props}>
                {children}
            </h3>
        ),
        ul: ({ children, ...props }) => (
            <ul className="mb-4 list-disc pl-8" {...props}>
                {children}
            </ul>
        ),
        ol: ({ children, ...props }) => (
            <ol className="mb-4 list-decimal pl-8 last:mb-0" {...props}>
                {children}
            </ol>
        ),
        li: ({ children, ...props }) => (
            <li className="mb-1" {...props}>
                {children}
            </li>
        ),
        a: ({ children, ...props }) => (
            <a className="text-content-link hover:underline" target="_blank" rel="noopener noreferrer" {...props}>
                {children}
            </a>
        ),
        blockquote: ({ children, ...props }) => (
            <blockquote className="mb-4 border-l-4 border-border-base py-1 pl-4 text-content-muted" {...props}>
                {children}
            </blockquote>
        ),
        table: ({ children, ...props }) => (
            <div className="mb-4 overflow-hidden overflow-x-auto rounded-lg border border-border-subtle">
                <table className="min-w-full" {...props}>
                    {children}
                </table>
            </div>
        ),
        thead: ({ children, ...props }) => (
            <thead className="border-b border-border-subtle bg-surface-muted" {...props}>
                {children}
            </thead>
        ),
        th: ({ children, ...props }) => (
            <th className="px-4 py-2 text-left text-sm font-semibold" {...props}>
                {children}
            </th>
        ),
        td: ({ children, ...props }) => (
            <td className="border-t border-border-subtle px-4 py-2 text-sm" {...props}>
                {children}
            </td>
        ),
        p: ({ node, children, ...props }) => {
            type MarkdownNode = {
                tagName?: string;
            };
            const childNodes = node?.children as MarkdownNode[];
            if (childNodes?.[0]?.tagName === "img" && childNodes.length === 1) {
                return <>{children}</>;
            }
            return (
                <p className="mb-4 last:mb-0" {...props}>
                    {children}
                </p>
            );
        },
        code: ({ children, ...props }) => {
            return (
                <code className="rounded-sm bg-surface-muted px-0.5 font-mono text-content-muted" {...props}>
                    {children}
                </code>
            );
        },
        pre: ({ children, ...props }) => {
            return (
                <pre
                    className="mb-4 overflow-x-auto rounded-md border border-border-subtle bg-surface-muted px-2 pb-1 pt-2 font-mono text-sm last:mb-0"
                    {...props}
                >
                    {children}
                </pre>
            );
        },
        img: ({ ...props }) => <img className="my-2 h-auto max-w-full rounded-md" {...props} alt={props.alt || ""} />,
        hr: () => <hr className="my-6 border-t border-border-strong" />,
        strong: ({ children, ...props }) => (
            <strong className="font-semibold" {...props}>
                {children}
            </strong>
        ),
        em: ({ children, ...props }) => (
            <em className="italic" {...props}>
                {children}
            </em>
        ),
    };

    return (
        <div
            className={cn(
                "[word-break:break-word]",
                "[overflow-wrap:anywhere]",
                className,
            )}
        >
            <ReactMarkdown components={components} remarkPlugins={[remarkGfm]}>
                {content}
            </ReactMarkdown>
        </div>
    );
};

export const MarkdownPreview = memo(MarkdownPreviewBase, (prevProps, nextProps) => {
    return prevProps.content === nextProps.content;
});
